import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface PulseChipProps extends Omit<BoxProps, 'children'> {
  /** Small leading status dot color, e.g. "var(--ds-cyan)" */
  dotColor?: string
  children: React.ReactNode
}

const Root = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  fontSize: 12.5,
  color: 'var(--ds-ink-muted)',
  '& b': { color: 'var(--ds-ink)', fontWeight: 640 },
})

const Dot = styled('span', {
  shouldForwardProp: (p) => p !== 'dotColor',
})<{ dotColor: string }>(({ dotColor }) => ({
  width: 7,
  height: 7,
  borderRadius: '50%',
  flexShrink: 0,
  background: dotColor,
}))

/** Single activity signal pill — colored dot + description with bold emphasis spans. */
export function PulseChip({ dotColor = 'var(--ds-accent)', children, ...rest }: PulseChipProps) {
  return (
    <Root {...rest}>
      <Dot dotColor={dotColor} />
      {children}
    </Root>
  )
}

export interface PulseRowProps extends Omit<BoxProps, 'children'> {
  children: React.ReactNode
}

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  padding: '12px 0 22px',
})

/** Flex row of `PulseChip`s — the library "what's happening" activity strip. */
export function PulseRow({ children, ...rest }: PulseRowProps) {
  return (
    <Row {...rest}>
      {children}
    </Row>
  )
}
